import React from "react";
import { Form, Col } from "react-bootstrap";

import ValueSelect from "../../../../layout/select";
import BaseModal from "../../../../layout/modal";
import TemplateCustomFieldInputs from "../../../../layout/template-custom-fields/inputs";
import TestData from "../../../../constants/datasets";
import { deepEqual } from "../../../../utils/object";
import {
    applyCustomValues,
    normalizeCustomFields,
    validateCustomValues,
} from "../../../../utils/template-custom-fields";

const LEAD_CRM_CAMPAIGN_DATASET = "Lead CRM Campaign";

const htmlEscape = (value) => `${value ?? ""}`
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const cloneData = (value) => JSON.parse(JSON.stringify(value ?? {}));

const previewOptOutUrl = (data = {}) => {
    const origin = window.location.origin.replace(/\/+$/, "");
    const params = new URLSearchParams({ preview: "1" });
    const instance = localStorage.getItem("instance") || "";
    const email = data?.Person?.email || data?.person?.email || "";

    if (instance) params.set("i", instance);
    if (email) params.set("email", email);

    return `${origin}/public/email/optout?${params.toString()}`;
};

const decorateLeadCampaignDataset = (datasetName, data) => {
    const result = cloneData(data);
    if (datasetName !== LEAD_CRM_CAMPAIGN_DATASET) return result;

    const url = previewOptOutUrl(result);
    const optOut = {
        ...(result.OptOut || {}),
        url,
        href: url,
        value: url,
        label: result.OptOut?.label || "Opt out",
        text: result.OptOut?.text || "Opt out of CRM email campaigns",
        html: `<a href="${htmlEscape(url)}">Opt out of CRM email campaigns</a>`
    };

    result.OptOut = optOut;
    result.optOut = { ...(result.optOut || {}), ...optOut };
    result.Unsubscribe = { ...(result.Unsubscribe || {}), ...optOut };
    result.unsubscribe = { ...(result.unsubscribe || {}), ...optOut };
    result.unsubscribeUrl = url;
    result.emailCampaignOptOutUrl = url;
    result.custom = {
        ...(result.custom || {}),
        optOutUrl: url,
        unsubscribeUrl: url,
        emailCampaignOptOutUrl: url
    };

    return result;
};

class TestModal extends React.Component {
    constructor(props) {
        super(props);

        this.datasets = Object.keys(TestData).map(key => ({
            label: key,
            value: key
        }));

        this.state = {
            configuration: props.configurations?.[0]?.name ?? "",
            dataset: props.dataset ?? this.datasets?.[0]?.value ?? "",
            to: props.to ?? "",
            custom: {},
            customErrors: {},
            submitting: false
        };
        this.submitting = false;
    }

    componentDidUpdate = (old) => {
        if (!deepEqual(old.configurations, this.props.configurations)) {
            this.setState({
                configuration: this.props.configurations?.[0]?.name ?? ""
            });
        } 

        if (this.props.show && !old.show) {
            this.submitting = false;
            this.setState({
                custom: {},
                customErrors: {},
                submitting: false
            });
        }
    }
    
    onTestOkModal = async () => {
        if (this.submitting) return;

        const { configuration, dataset, to, custom } = this.state;
        const customFields = normalizeCustomFields(this.props.customFields);
        const validation = validateCustomValues(customFields, custom);

        if (!validation.valid) {
            this.setState({ customErrors: validation.errors });
            return;
        }

        const result = {
            dataset: decorateLeadCampaignDataset(
                dataset,
                applyCustomValues(TestData[dataset], custom, customFields)
            ),
            custom
        };

        if (this.props.configuration) result.configuration = configuration;
        if (this.props.recipient) result.to = to;
        
        if (this.props.onOk) {
            this.submitting = true;
            this.setState({ submitting: true });

            try {
                await this.props.onOk(result);
            } catch {
            } finally {
                this.submitting = false;
                this.setState({ submitting: false });
            }
        }
    }

    onTestHideModal = () => {
        if (this.props.onCancel) this.props.onCancel();
    }

    onChange = (e) => {
        this.setState({
            [e.target.name]: e.target.value
        });
    }

    onCustomChange = (name, value) => {
        this.setState(prev => ({
            custom: {
                ...prev.custom,
                [name]: value
            },
            customErrors: {
                ...prev.customErrors,
                [name]: undefined
            }
        }));
    }

    render() {
        if (!this.props.show) return <></>;
        
        const configs = (this.props.configurations || []).map(c => ({
            label: c.name,
            value: c.name,
        }));
        const disabled = this.state.submitting;

        return (
            <BaseModal
                show={this.props.show}
                title="Test template"
                buttons={[
                    { caption: disabled ? "testing" : "ok", onEnabledClick: this.onTestOkModal, variant: "primary", disabledVariant: "secondary", enabled: !disabled, display: true },
                    { default: true, caption: "cancel", onEnabledClick: this.onTestHideModal, variant: "primary", enabled: true, display: true },
                ]}
            >
                <>
                    {this.props.configuration ?
                        <Col xs="12">
                            <div className="mb-3 top-label">
                                <ValueSelect
                                    name="configuration"
                                    selected={this.state.configuration ?? ""}
                                    onChange={this.onChange}
                                    options={configs}
                                />
                                <Form.Label>CONFIGURATION</Form.Label>
                            </div>
                        </Col>
                        : ``}
                    <Col xs="12">
                        <div className="mb-3 top-label">
                            <ValueSelect
                                name="dataset"
                                selected={this.state.dataset ?? ""}
                                onChange={this.onChange}
                                options={this.datasets}
                            />
                            <Form.Label>DATASET</Form.Label>
                        </div>
                    </Col>
                    {this.props.recipient ?
                        <Col xs="12">
                            <div className="mb-3 top-label">
                                <Form.Control type="text" name="to" value={this.state.to} onChange={this.onChange} />
                                <Form.Label>SEND TO</Form.Label>
                            </div>
                        </Col>
                        : ``}
                    <TemplateCustomFieldInputs
                        fields={this.props.customFields}
                        values={this.state.custom}
                        errors={this.state.customErrors}
                        onChange={this.onCustomChange}
                    />
                </>
            </BaseModal>
        )
    }
}

export default TestModal;
